import { IActivity } from '@eduwrite/shared';

/**
 * Transforms an activity title into a student-safe, spoiler-free title.
 * Prevents arithmetic answers, spelling solutions, tricky words, or sentences
 * from being leaked in task preview cards, header pills, or activity headers.
 */
export function getStudentSafeActivityTitle(
    activity: Pick<IActivity, 'title' | 'type' | 'config'> | null | undefined,
    fallbackTitle = 'Activity'
): string {
    if (!activity) return fallbackTitle;

    const rawTitle = (activity.title || '').trim();
    const type = (activity.type || '').toLowerCase();

    // 1. Spelling: never leak the word in the title (e.g. "Spell: cat" -> "Spelling Practice", "Spell cat" -> "Spelling Practice")
    if (type === 'spelling') {
        const lower = rawTitle.toLowerCase();
        if (
            !rawTitle ||
            lower.startsWith('spell:') ||
            (lower.startsWith('spell ') && !lower.startsWith('spell the') && !lower.startsWith('spell your')) ||
            (activity.config && (activity.config as any).word && lower.includes(String((activity.config as any).word).toLowerCase()))
        ) {
            return 'Spelling Practice';
        }
        return rawTitle;
    }

    // 2. Common Exception / Tricky Words: never leak the word (e.g. 'Remember: "the"' -> "Tricky Words Memory")
    if (type === 'common_exception_words' || type === 'special_words') {
        const lower = rawTitle.toLowerCase();
        if (
            !rawTitle ||
            lower.startsWith('remember:') ||
            lower.startsWith('remember ') ||
            rawTitle.includes('"') ||
            (activity.config && (activity.config as any).word && lower.includes(String((activity.config as any).word).toLowerCase()))
        ) {
            return 'Tricky Words Memory';
        }
        return rawTitle;
    }

    // 3. Sentence Building: never leak the target sentence (e.g. 'Build: "The cat sat on the mat"' -> "Sentence Building")
    if (type === 'sentence_building' || type === 'sentence_build') {
        const lower = rawTitle.toLowerCase();
        if (!rawTitle || lower.startsWith('build:') || lower.startsWith('build ') || rawTitle.includes('"')) {
            return 'Sentence Building';
        }
        return rawTitle;
    }

    // 4. Dictation: never leak the dictation target text
    if (type === 'dictation') {
        const lower = rawTitle.toLowerCase();
        if (!rawTitle || lower.startsWith('dictat:') || rawTitle.includes('"')) {
            return 'Dictation Practice';
        }
        return rawTitle;
    }

    // 5. Maths: clean up formulas, number bonds, equations, and answers
    if (type === 'maths' || type === 'math') {
        const lower = rawTitle.toLowerCase();

        if (lower.includes('number bond')) {
            return 'Number Bonds to 10';
        }
        if (lower.includes('missing number')) {
            return 'Missing Number Challenge';
        }
        if (lower.includes('double')) {
            return 'Doubles Practice';
        }
        if (lower.includes('counting in') || lower.includes('counting')) {
            return 'Counting Patterns';
        }
        if (lower.includes('one more') || lower.includes('one less')) {
            return 'Number Relationships';
        }
        if (lower.includes('times') || lower.includes('multiply') || lower.includes('×') || lower.includes('*')) {
            return 'Times Tables Practice';
        }
        if (lower.includes('divide') || lower.includes('÷') || lower.includes('/')) {
            return 'Division Practice';
        }
        if (lower.startsWith('add:') || lower.includes('add') || lower.includes('+')) {
            return 'Addition Practice';
        }
        if (lower.startsWith('subtract:') || lower.includes('subtract') || lower.includes('-')) {
            return 'Subtraction Practice';
        }
        if (lower.includes('hard:') || lower.includes('challenge')) {
            return 'Maths Challenge';
        }
        // If it looks like an equation (e.g., "5 + 3 = ?")
        if (/[\d\s+\-×÷=/?]+/.test(rawTitle) && rawTitle.length < 20) {
            return 'Maths Practice';
        }
        if (!rawTitle) {
            return 'Maths Practice';
        }
        return rawTitle;
    }

    // 6. Letter Tracing / Letter Formation: safe to show letter (e.g., "Trace Letter A")
    if (type === 'letter_tracing' || type === 'letter_formation' || type === 'tracing') {
        return rawTitle || 'Letter Tracing';
    }

    return rawTitle || fallbackTitle;
}
